/**
 * Class to implement the focus-trap functionality,
 * which can be used by any other code to build upon
 */
export class FocusTrap {
    private initial?: HTMLElement;
    private focusable: NodeListOf<HTMLElement>;
    private first?: HTMLElement;
    private last?: HTMLElement;

    /**
     * The constructor to setup the focus trap
     *
     * @param {HTMLElement} container
     */
    public constructor(private container: HTMLElement) {
        this.focusable = this.container.querySelectorAll<HTMLElement>(
            [
                "a[href]",
                "button:not([disabled])",
                "textarea:not([disabled])",
                "input:not([disabled])",
                "select:not([disabled])",
                "[tabindex]:not([tabindex='-1'])",
            ].join(","),
        );

        if (this.focusable.length > 0) {
            this.first = this.focusable[0];
            this.last = this.focusable[this.focusable.length - 1];
        }
    }

    /**
     * Method to set the initial element in cases, where
     * something might be opened with hover event and therefore
     * without focus trap but closed with key elements, where
     * one would want to refocus the previous element
     *
     * @param {HTMLElement} initial
     * @returns {void}
     */
    public setInitial(initial: HTMLElement): void {
        this.initial = initial;
    }

    /**
     * Method to enable the focus trap
     *
     * @param {HTMLElement | null} initial
     * @returns {void}
     */
    public enable(initial?: HTMLElement): void {
        this.container.addEventListener("keydown", this.listener);
        this.initial = initial;

        requestAnimationFrame(() => (this.first ?? this.container).focus());
    }

    /**
     * Method to disable the focus trap
     *
     * @param {boolean} [refocus=true]
     * @returns {void}
     */
    public disable(refocus: boolean = true): void {
        if (refocus) this.initial?.focus();
        this.container.removeEventListener("keydown", this.listener);
    }

    /**
     * Method to handle the logic of the listener
     *
     * @returns {void}
     */
    protected listener = (event: KeyboardEvent): void => {
        if (event.key !== "Tab") return;

        if (this.focusable.length === 0) {
            return;
        }

        if (event.shiftKey) {
            if (document.activeElement === this.first) {
                this.last?.focus();
                event.preventDefault();
            }
        } else {
            if (document.activeElement === this.last) {
                this.first?.focus();
                event.preventDefault();
            }
        }
    };
}
